// Learnoza Brand Theme Configuration
// Modern Educational Consultancy Brand Colors

export const theme = {
  // Primary Brand Colors
  colors: {
    // Main brand colors from the new design
    primary: '#267A8D',      // Teal - Main brand color
    primaryDark: '#1a5a6e',  // Dark Teal - Hover states
    primaryLight: '#3a9db3', // Light Teal - Accents

    secondary: '#FEC409',    // Gold/Yellow - Accent color
    secondaryDark: '#e5b008', // Dark Gold - Hover
    secondaryLight: '#ffd54f', // Light Gold - Highlights

    neutral: '#A5A196',      // Gray - Secondary text
    neutralDark: '#7d7d72',  // Dark Gray
    neutralLight: '#c4c4ba', // Light Gray

    // Background colors
    white: '#FFFFFF',
    background: '#F8F9FA',   // Soft off-white background
    backgroundAlt: '#F1F2F3', // Alternative background
    backgroundDark: '#E8E9EA', // Darker background for sections

    // Text colors
    textPrimary: '#267A8D',  // Teal for headings
    textSecondary: '#555555', // Dark gray for body
    textMuted: '#A5A196',    // Muted gray
    textDark: '#1a1a1a',     // Near black
    textLight: '#FFFFFF',    // White text

    // Accent colors
    success: '#10B981',      // Green for success states
    warning: '#FEC409',      // Yellow/Gold for warnings
    error: '#EF4444',        // Red for errors
    info: '#267A8D',         // Teal for info

    // Gradient definitions
    gradients: {
      primary: 'linear-gradient(135deg, #267A8D 0%, #1a5a6e 100%)',
      secondary: 'linear-gradient(135deg, #FEC409 0%, #faac18 100%)',
      hero: 'linear-gradient(135deg, #267A8D 0%, #224563 50%, #003252 100%)',
      gold: 'linear-gradient(135deg, #ffe7a3 0%, #ffca05 50%, #faac18 100%)',
      stats: 'linear-gradient(135deg, #267A8D 0%, #224563 100%)',
      card: 'linear-gradient(135deg, #267A8D 0%, #1a5a6e 100%)',
      text: 'linear-gradient(90deg, #FEC409 0%, #267A8D 100%)',
    }
  },

  // Typography
  fonts: {
    primary: '"Motiva Sans Light", "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    heading: '"Motiva Sans Bold", "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    mono: '"Fira Code", "Monaco", "Consolas", monospace',
  },

  // Font sizes
  fontSizes: {
    xs: '0.75rem',    // 12px
    sm: '0.875rem',   // 14px
    base: '1rem',     // 16px
    lg: '1.125rem',   // 18px
    xl: '1.25rem',    // 20px
    '2xl': '1.5rem',  // 24px
    '3xl': '1.875rem', // 30px
    '4xl': '2.25rem', // 36px
    '5xl': '3rem',    // 48px
    '6xl': '3.75rem', // 60px
  },

  // Spacing
  spacing: {
    xs: '0.25rem',
    sm: '0.5rem',
    md: '1rem',
    lg: '1.5rem',
    xl: '2rem',
    '2xl': '3rem',
    '3xl': '4rem',
    '4xl': '6rem',
  },

  // Border radius
  borderRadius: {
    sm: '4px',
    md: '8px',
    lg: '12px',
    xl: '16px',
    '2xl': '24px',
    full: '9999px',
  },

  // Shadows
  shadows: {
    sm: '0 1px 2px rgba(0, 0, 0, 0.05)',
    md: '0 4px 6px rgba(0, 0, 0, 0.1)',
    lg: '0 10px 15px rgba(0, 0, 0, 0.1)',
    xl: '0 20px 25px rgba(0, 0, 0, 0.15)',
    '2xl': '0 25px 50px rgba(0, 0, 0, 0.25)',
    glow: '0 0 20px rgba(38, 122, 141, 0.3)',
    goldGlow: '0 0 20px rgba(254, 196, 9, 0.3)',
  },

  // Transitions
  transitions: {
    fast: '0.15s ease-in-out',
    normal: '0.3s ease-in-out',
    slow: '0.5s ease-in-out',
    bounce: '0.5s cubic-bezier(0.68, -0.55, 0.265, 1.55)',
  },

  // Breakpoints
  breakpoints: {
    xs: '320px',
    sm: '576px',
    md: '768px',
    lg: '992px',
    xl: '1200px',
    '2xl': '1400px',
  },

  // Z-index layers
  zIndex: {
    dropdown: 1000,
    sticky: 1020,
    fixed: 1030,
    modal: 1040,
    popover: 1050,
    tooltip: 1060,
  },
};

// CSS Custom Properties for easy use in CSS files
export const cssVariables = `
  :root {
    /* Primary Colors */
    --color-primary: ${theme.colors.primary};
    --color-primary-dark: ${theme.colors.primaryDark};
    --color-primary-light: ${theme.colors.primaryLight};

    /* Secondary Colors */
    --color-secondary: ${theme.colors.secondary};
    --color-secondary-dark: ${theme.colors.secondaryDark};
    --color-secondary-light: ${theme.colors.secondaryLight};

    /* Neutral Colors */
    --color-neutral: ${theme.colors.neutral};
    --color-neutral-dark: ${theme.colors.neutralDark};
    --color-neutral-light: ${theme.colors.neutralLight};

    /* Background Colors */
    --color-white: ${theme.colors.white};
    --color-background: ${theme.colors.background};
    --color-background-alt: ${theme.colors.backgroundAlt};
    --color-background-dark: ${theme.colors.backgroundDark};

    /* Text Colors */
    --color-text-primary: ${theme.colors.textPrimary};
    --color-text-secondary: ${theme.colors.textSecondary};
    --color-text-muted: ${theme.colors.textMuted};
    --color-text-dark: ${theme.colors.textDark};
    --color-text-light: ${theme.colors.textLight};

    /* Gradients */
    --gradient-primary: ${theme.colors.gradients.primary};
    --gradient-secondary: ${theme.colors.gradients.secondary};
    --gradient-hero: ${theme.colors.gradients.hero};
    --gradient-gold: ${theme.colors.gradients.gold};
    --gradient-stats: ${theme.colors.gradients.stats};
    --gradient-card: ${theme.colors.gradients.card};
    --gradient-text: ${theme.colors.gradients.text};

    /* Shadows */
    --shadow-sm: ${theme.shadows.sm};
    --shadow-md: ${theme.shadows.md};
    --shadow-lg: ${theme.shadows.lg};
    --shadow-xl: ${theme.shadows.xl};
    --shadow-glow: ${theme.shadows.glow};
    --shadow-gold-glow: ${theme.shadows.goldGlow};

    /* Transitions */
    --transition-fast: ${theme.transitions.fast};
    --transition-normal: ${theme.transitions.normal};
    --transition-slow: ${theme.transitions.slow};
    --transition-bounce: ${theme.transitions.bounce};

    /* Border Radius */
    --radius-sm: ${theme.borderRadius.sm};
    --radius-md: ${theme.borderRadius.md};
    --radius-lg: ${theme.borderRadius.lg};
    --radius-xl: ${theme.borderRadius.xl};
    --radius-2xl: ${theme.borderRadius['2xl']};
    --radius-full: ${theme.borderRadius.full};
  }
`;

export default theme;
